import { mount } from 'svelte';

/* ═══ WHERE THIS BUILD'S DATA COMES FROM ═══
 *
 * Declared first, before anything can fetch. This is the line that makes the
 * whole extraction worth doing: the same layer modules the boat runs, told a
 * different set of hosts.
 *
 *   static-data  the shipped year of CHS tides and streams, the DFO subareas
 *                and closures, the EC zone polygons — served with this build
 *   marine-data  the layer cache's feeds — wind, tides, weather. Absent
 *                without VITE_LAYER_CACHE, and every layer that declared
 *                `requires: ['marine-data']` then greys out in the panel saying
 *                so, which is the honest answer and not an error
 *   chart-cache  the layer cache's tile half. Absent, the chart layer goes
 *                direct to the WMS — a branch that was always there, because
 *                the boat uses it whenever the pi's proxy is unreachable
 *   signalk      ABSENT. No vessel, no track, no routes to write
 *
 * ONE variable points at both, because they are one service: services/layer-cache
 * serves tiles at /tiles and everything else at /feed/{id}. Set VITE_LAYER_CACHE
 * and the chart starts caching and the live layers light up, with no other
 * change anywhere.
 */
import { chartSources, configureSources } from '@gennetay/chart-core/sources';

configureSources(
  chartSources({
    'static-data': `${import.meta.env.BASE_URL}data`,
    /* ═══ NOT THE LAYER CACHE, YET ═══
     *
     * This was pointed at VITE_LAYER_CACHE and it was a lie the panel then
     * repeated. The stores that use `marine-data` ask for the PI's route names
     * — /seacurrents, /seasurface — and the layer cache serves its feeds at
     * /feed/{id} and answers 404 for those. So the two model layers stopped
     * saying "no marine-data provider on this host", which is true and
     * actionable, and started saying "waiting for the field from the pi…" on a
     * host that has no pi and never will. Seen on the deployed chart.
     *
     * It goes back to pointing here when those feeds exist on the layer cache
     * with the shape the stores expect. Until then, absent is the honest
     * answer. */
    'marine-data': import.meta.env.VITE_MARINE_DATA ?? null,
    /* Point this at a deployed services/tile-cache and three things change at
     * once with no other edit: the chart layer routes through the proxy and its
     * IndexedDB cache instead of going direct, the cache panel starts reporting
     * the boat-side figures, and a pan stops being a fresh download. Absent, it
     * all still works — just without a cache anywhere. */
    'chart-cache': import.meta.env.VITE_LAYER_CACHE
      ? `${import.meta.env.VITE_LAYER_CACHE}/tiles`
      : null,
    /* The layer cache's FEED API — /feeds to discover, /feed/{id} to ask. The
     * same deployment that serves the tiles; a different shape, so a different
     * name. This is what lights up the global wind forecast. */
    feeds: import.meta.env.VITE_LAYER_CACHE ?? null,
    signalk: null
  })
);

/* The shared design system FIRST — tokens and faces — then this app's own few
 * rules, which reference them. */
import '@gennetay/chart-ui/theme.css';
import './styles.css';
import App from './App.svelte';

/* ═══ AND THE SAFARI-ONLY HALF ═══
 *
 * iOS Safari ignores `user-scalable=no` — deliberately, since iOS 10, because
 * suppressing zoom is usually a fault. It honours `touch-action`, which covers
 * the common case, but it ALSO emits its own `gesture*` events for a pinch and
 * will still scale the page from them on older versions. These three lines are
 * the only thing that reliably stops a pinch beginning on a button from zooming
 * the interface out from under the chart.
 *
 * Non-passive on purpose: a passive listener cannot preventDefault, and these
 * exist for nothing else. Unknown to every other engine, where adding them
 * costs one no-op registration each. */
for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(type, (e) => e.preventDefault(), { passive: false });
}

mount(App, { target: document.getElementById('app')! });
